import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Eye, ImagePlus, Plus, Presentation, Trash2 } from "lucide-react";
import { Input, Switch } from "@/components/ui-kit";
import { OnboardingSlides, Slide } from "@/components/Onboarding";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

const SlideEditor = ({ s, n, total, set, move, remove, canEdit }: { s: Slide; n: number; total: number; set: (s: Slide) => void; move: (d: number) => void; remove: () => void; canEdit: boolean }) => {
  const { t } = usePrefs();
  const [busy, setBusy] = useState(false);
  const upload = async (f?: File) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try { const { data } = await api.post("/admin/uploads", fd); set({ ...s, image: data.url }); } catch (e) { toast.error(errorText(e, t)); } finally { setBusy(false); }
  };
  const field = (k: keyof Slide, label: string, area = false, ltr = false) => (
    <label className="text-xs font-bold space-y-1 block">{label}
      {area ? <textarea value={s[k]} disabled={!canEdit} dir={ltr ? "ltr" : "rtl"} maxLength={300} rows={2} onChange={(e) => set({ ...s, [k]: e.target.value })} data-testid={`onb-${k}-${n}`} className="w-full rounded-2xl border bg-background px-3 py-2 text-sm font-normal" />
        : <Input value={s[k]} disabled={!canEdit} dir={ltr ? "ltr" : "rtl"} maxLength={80} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ ...s, [k]: e.target.value })} data-testid={`onb-${k}-${n}`} className="h-10 rounded-2xl" />}
    </label>
  );
  return (
    <li data-testid={`onb-slide-${n}`} className="rounded-2xl border p-3 flex gap-3">
      <label className={`relative w-20 shrink-0 aspect-[2/3] rounded-xl overflow-hidden bg-muted grid place-items-center ${canEdit ? "cursor-pointer" : ""}`}>
        {s.image ? <img src={s.image} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5 text-muted-foreground" />}
        {busy && <span className="absolute inset-0 bg-black/50 grid place-items-center text-white text-xs">…</span>}
        {canEdit && <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="sr-only" data-testid={`onb-image-${n}`} onChange={(e) => upload(e.target.files?.[0])} />}
      </label>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-black text-primary">{t("onb.slide").replace("{n}", String(n + 1))}</span>
          {canEdit && (
            <div className="flex gap-1">
              <button disabled={n === 0} onClick={() => move(-1)} aria-label={t("onb.up")} data-testid={`onb-up-${n}`} className="h-8 w-8 grid place-items-center rounded-full border disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
              <button disabled={n === total - 1} onClick={() => move(1)} aria-label={t("onb.down")} data-testid={`onb-down-${n}`} className="h-8 w-8 grid place-items-center rounded-full border disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
              <button disabled={total === 1} onClick={remove} aria-label={t("onb.delete")} data-testid={`onb-delete-${n}`} className="h-8 w-8 grid place-items-center rounded-full border text-destructive disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
            </div>
          )}
        </div>
        {field("title_ar", t("onb.titleAr"))}
        {field("body_ar", t("onb.bodyAr"), true)}
        <details className="text-xs"><summary className="cursor-pointer font-bold text-muted-foreground">{t("onb.english")}</summary>
          <div className="mt-2 space-y-2">{field("title_en", t("onb.titleEn"), false, true)}{field("body_en", t("onb.bodyEn"), true, true)}</div>
        </details>
      </div>
    </li>
  );
};

export const OnboardingSettings = ({ canEdit }: { canEdit: boolean }) => {
  const { t } = usePrefs();
  const [enabled, setEnabled] = useState(true);
  const [slides, setSlides] = useState<Slide[] | null>(null);
  const [preview, setPreview] = useState(false);
  useEffect(() => { api.get("/onboarding").then((r) => { setEnabled(r.data.enabled); setSlides(r.data.slides); }).catch(() => {}); }, []);
  if (!slides) return null;
  const upd = (i: number, s: Slide) => setSlides(slides.map((x, k) => (k === i ? s : x)));
  const move = (i: number, d: number) => { const c = [...slides]; [c[i], c[i + d]] = [c[i + d], c[i]]; setSlides(c); };
  const save = async () => {
    const blank = slides.findIndex((s) => !s.title_ar.trim());
    if (blank >= 0) return toast.error(t("onb.needTitle").replace("{n}", String(blank + 1)));
    try { const { data } = await api.put("/admin/onboarding", { enabled, slides }); setSlides(data.slides); toast.success(t("onb.saved")); } catch (e) { toast.error(errorText(e, t)); }
  };
  return (
    <div data-testid="onboarding-settings" className="rounded-3xl border bg-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3">
          <Presentation className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div><p className="font-bold">{t("onb.title")}</p><p className="text-sm text-muted-foreground">{t("onb.note")}</p></div>
        </div>
        <Switch checked={enabled} disabled={!canEdit} onCheckedChange={setEnabled} data-testid="onb-enabled" aria-label={t("onb.title")} />
      </div>
      <ul className="space-y-2">
        {slides.map((s, i) => <SlideEditor key={s.id} s={s} n={i} total={slides.length} canEdit={canEdit} set={(x) => upd(i, x)} move={(d) => move(i, d)} remove={() => setSlides(slides.filter((_, k) => k !== i))} />)}
      </ul>
      <div className="flex flex-wrap gap-2">
        {canEdit && slides.length < 6 && <button onClick={() => setSlides([...slides, { id: `s${Date.now().toString(36)}`, image: "", title_ar: "", title_en: "", body_ar: "", body_en: "" }])} data-testid="onb-add" className="h-10 px-4 rounded-full border text-sm font-bold inline-flex items-center gap-1.5"><Plus className="h-4 w-4" />{t("onb.add")}</button>}
        <button onClick={() => setPreview(true)} data-testid="onb-preview" className="h-10 px-4 rounded-full border text-sm font-bold inline-flex items-center gap-1.5"><Eye className="h-4 w-4" />{t("onb.preview")}</button>
        {canEdit && <button onClick={save} data-testid="onb-save" className="h-10 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold">{t("common.save")}</button>}
      </div>
      {preview && <OnboardingSlides slides={slides} onClose={() => setPreview(false)} />}
    </div>
  );
};
